import React, { useState } from 'react';
import { Info, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useOptionalSettings } from '@/components/posture-analysis/lib/optional-hooks';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import { getLaymanViewLabel } from '@/components/posture-analysis/self-assessment/view-display-labels';

interface PhotographyTipsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PhotographyTipsDrawer: React.FC<PhotographyTipsDrawerProps> = ({ isOpen, onClose }) => {
  const { laymanMode, allowedViews } = usePostureMode();
  const { settings, setSettings } = useOptionalSettings();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) return null;

  const handleAcknowledge = () => {
    if (dontShowAgain) {
      setSettings(prev => ({ ...prev, showPostureAnalysisTips: false }));
    }
    onClose();
  };

  return (
    <div className="border-t bg-primary/10 dark:bg-primary/20 border-primary/20 dark:border-primary/40 -mx-4 px-4 py-3">
      <div className="flex items-start gap-3">
        <Info className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">
                📸 Photography Tips for Accurate Posture Analysis
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Set camera zoom to <strong>≥1×</strong> to avoid wide-angle lens distortion</li>
                <li>Ensure the subject fills the frame from head to feet</li>
                <li>Maintain consistent distance for all views ({laymanMode ? allowedViews.map((v) => getLaymanViewLabel(v).toLowerCase()).join(', ') : 'Anterior, Right, Posterior, Left'})</li>
                <li>Use good lighting to ensure clear visibility of body landmarks</li>
                <li>Keep the camera level and perpendicular to the subject</li>
              </ul>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleAcknowledge}
              className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
              aria-label="Close tips"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-primary/20">
            <div className="flex items-center gap-2">
              <Checkbox
                id="dont-show-again"
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(checked === true)}
                className="h-4 w-4"
              />
              <Label
                htmlFor="dont-show-again"
                className="text-xs text-muted-foreground cursor-pointer"
              >
                Don't show this again
              </Label>
            </div>
            <Button
              size="sm"
              onClick={handleAcknowledge}
              className="h-8 px-3 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

